/**
 * `/login/totp-enrol` — ENG-09 (T-1.17). The forced TOTP-enrolment step that follows
 * invite-accept (or an enrolment-required sign-in), before the firm picker (auth-and-authz.md
 * §4/§5; worker-auth fixtures: totp-enrol, totp-confirm, totp-recovery-codes).
 *
 *  1. POST /auth/totp/enrol → { secret, otpauth_uri } (returned ONCE).
 *  2. The QR is rendered CLIENT-SIDE from the provisioning URI (the secret is never
 *     re-sent over the wire after enrolment — §5). The manual secret + the issuer parsed from
 *     the URI (worker-auth's identity-global factor uses the fixed issuer `ARGUS`) are shown.
 *  3. The user proves a live code → POST /auth/totp/confirm. This is FIRST-TIME activation (the
 *     identity is not yet enrolled), which needs no prior factor and records `totp` on the
 *     confirming session — so e.g. a second invitation can be accepted without re-signing in.
 *  4. ONLY once confirm has succeeded, the 10 one-time recovery codes are requested
 *     (POST /auth/totp/recovery-codes requires an enrolled identity AND a session that proved the
 *     factor — asking earlier is refused `second_factor_required`). They are shown ONCE with a
 *     "save these now" affordance, a download and a copy; they are NOT retrievable again (leaving
 *     the screen loses them — there is no re-fetch path in the UI). While they are on screen and
 *     the user has not ticked "I have saved…", closing or reloading the tab is challenged with
 *     the browser's `beforeunload` prompt (the tick already gates Continue; the guard is for the
 *     tab).
 *
 * Confirm refusals (`totpRefusal`), each with its own non-revealing copy:
 *  - 401 `second_factor_required` — the identity became enrolled elsewhere (another window or
 *    device) while this session is password-only: sign in again with the authenticator.
 *  - 409 `conflict` — the identity is ALREADY enrolled with nothing pending (e.g. an earlier
 *    confirm on this session committed but its response or the codes request was lost). worker-auth
 *    answers 409 only to a session that has proven the factor (a password-only one gets the 401
 *    above first), so enrolment is treated as done and the screen goes straight on to the
 *    recovery codes; if even those are refused `second_factor_required` (a concurrent confirm
 *    elsewhere won the activation race), it is the sign-in-again path.
 *  - 429 `account_locked` — too many wrong codes (login's shared lock): try again later.
 *  - anything else (the uniform 400 for a wrong / reused code) — its message; retry.
 * Once enrolment is active the screen NEVER returns to the scan step (a second confirm could only
 * be refused): a failed recovery-codes request is retried on its own ("Try again").
 *
 * Afterwards → the firm picker (/firms), or back to the invitation the sign-in started from.
 * The already-enrolled guard lives in the route's `beforeLoad` (redirects to /firms); a
 * defensive 409 on enrol here also routes away.
 */
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useRef, useState, type FormEvent, type JSX } from 'react';

import {
  AuthCard,
  CheckboxField,
  FormError,
  FormStatus,
  SecondaryButton,
  SubmitButton,
  TextField,
} from '../components/ui.tsx';
import {
  api,
  getErrorMessage,
  isApiError,
  totpRefusal,
  type TotpEnrolResponse,
} from '../lib/api.ts';
import { carriedRedirect, useAfterSignIn } from '../router/redirect.ts';
import { useAuth } from '../store/auth-store.ts';

type Phase =
  | 'enrolling'
  | 'scan'
  | 'saving'
  /** Confirm succeeded; the one-time recovery codes are being fetched (or failed to be). */
  | 'codes'
  | 'recovery'
  /** Refused 401 `second_factor_required`: this password-only session cannot finish here. */
  | 'sign_in_again';

/** Confirm (or the recovery codes) refused 401 `second_factor_required` during enrolment. */
export const ENROLLED_ELSEWHERE_COPY =
  'Two-factor authentication has already been set up for this account. Sign in again with your authenticator app (or a recovery code) to continue.';
/** Confirm 429 `account_locked`. */
export const ENROL_CODE_LOCKED_COPY =
  'Too many incorrect codes. For your security, code entry is paused for a while. Please try again later.';
/** The download could not be started (no object URLs, no `data:` navigation). */
export const DOWNLOAD_UNAVAILABLE_COPY =
  'Download is not available in this browser; copy the codes instead.';
/** The clipboard is unavailable (an insecure context, or a browser without the API) or refused. */
export const COPY_FAILED_COPY =
  "Couldn't copy the codes. Select them on screen and copy them manually.";
export const COPIED_COPY = 'Codes copied to the clipboard. Paste them somewhere safe now.';
export const DOWNLOADED_COPY = 'Download started. Keep the file somewhere safe.';

/** What the download / copy produce. */
function recoveryCodesText(codes: string[]): string {
  return `ARGUS recovery codes\n\n${codes.join('\n')}\n`;
}

/** Parse the issuer (worker-auth's fixed `ARGUS`) out of the otpauth provisioning URI. */
function parseIssuer(uri: string): string | null {
  try {
    return new URL(uri).searchParams.get('issuer');
  } catch {
    return null;
  }
}

/** Render the provisioning URI to an inline SVG QR, entirely client-side (no secret round-trip). */
async function renderQrSvg(uri: string): Promise<string> {
  const qrcode = await import('qrcode');
  return qrcode.default.toString(uri, { type: 'svg', margin: 1, width: 200 });
}

export function TotpEnrolScreen(): JSX.Element {
  const navigate = useNavigate();
  const afterSignIn = useAfterSignIn();
  const { redirect } = useSearch({ strict: false }) as { redirect?: string };
  const refresh = useAuth((s) => s.refresh);

  const [phase, setPhase] = useState<Phase>('enrolling');
  const [enrol, setEnrol] = useState<TotpEnrolResponse | null>(null);
  const [qrSvg, setQrSvg] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [saved, setSaved] = useState(false);
  // Feedback for the download / copy actions on the recovery-codes step.
  const [codesNotice, setCodesNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(
    null,
  );
  const startedRef = useRef(false);

  // Step 1 — begin enrolment exactly once.
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    void (async () => {
      try {
        const res = await api.auth.totpEnrol();
        setEnrol(res);
        setQrSvg(await renderQrSvg(res.otpauth_uri));
        setPhase('scan');
      } catch (err) {
        if (isApiError(err) && err.status === 409) {
          // Already enrolled — nothing to do here.
          await afterSignIn(redirect);
          return;
        }
        setError(getErrorMessage(err));
      }
    })();
    // startedRef keeps this to ONE enrol POST however often the effect re-runs.
  }, [afterSignIn, redirect]);

  // The leave guard: while the one-time codes are on screen and not yet marked as saved, closing
  // or reloading the tab gets the browser's "leave site?" prompt. Removed the moment the box is
  // ticked (or the step is left), so a user who has saved them is never nagged.
  const codesUnsaved = phase === 'recovery' && recoveryCodes !== null && !saved;
  useEffect(() => {
    if (!codesUnsaved) return;
    const warn = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      // Legacy browsers need a (non-empty) returnValue to show the prompt; modern ones ignore it.
      e.returnValue = 'Your recovery codes have not been saved.';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [codesUnsaved]);

  /**
   * Step 4 — the one-time recovery codes. Called ONLY once enrolment is active on a session that
   * proved the factor (confirm 200, or confirm's 409 "already enrolled").
   */
  async function loadRecoveryCodes(): Promise<void> {
    setError(null);
    setPhase('codes');
    try {
      const rc = await api.auth.totpRecoveryCodes();
      setRecoveryCodes(rc.recovery_codes);
      setPhase('recovery');
    } catch (err) {
      if (totpRefusal(err) === 'second_factor_required') {
        setPhase('sign_in_again');
        return;
      }
      // Enrolment IS active: stay on this step (retry just the codes), never back to scan.
      setError(getErrorMessage(err));
    }
  }

  async function handleConfirm(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setPhase('saving');
    try {
      await api.auth.totpConfirm({ code: code.trim() });
    } catch (err) {
      switch (totpRefusal(err)) {
        case 'second_factor_required':
          setPhase('sign_in_again');
          return;
        case 'conflict':
          // Already enrolled, nothing pending — an earlier confirm committed. Fall through to
          // the recovery codes (the gate there is the authority on this session's factor).
          break;
        case 'account_locked':
          setError(ENROL_CODE_LOCKED_COPY);
          setPhase('scan');
          return;
        default:
          setError(getErrorMessage(err));
          setPhase('scan');
          return;
      }
    }
    // Enrolment is now active and THIS session carries the factor — only now may the
    // factor-gated recovery codes be requested.
    await refresh(); // totp_enrolled is now true
    await loadRecoveryCodes();
  }

  async function signInAgain(): Promise<void> {
    await navigate({ to: '/login', search: carriedRedirect(redirect) });
  }

  /**
   * Save the codes as a text file. An object URL where the browser has them; otherwise a `data:`
   * URL (jsdom, and any runtime without `URL.createObjectURL`) — never a silent no-op: if neither
   * can be started, the user is told to copy instead.
   */
  function downloadCodes(): void {
    if (!recoveryCodes) return;
    const text = recoveryCodesText(recoveryCodes);
    try {
      const a = document.createElement('a');
      a.download = 'argus-recovery-codes.txt';
      if (typeof URL.createObjectURL === 'function') {
        const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
        a.href = url;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        a.href = `data:text/plain;charset=utf-8,${encodeURIComponent(text)}`;
        a.click();
      }
      setCodesNotice({ tone: 'ok', text: DOWNLOADED_COPY });
    } catch {
      setCodesNotice({ tone: 'error', text: DOWNLOAD_UNAVAILABLE_COPY });
    }
  }

  /** Copy the codes to the clipboard, saying whether it worked (the API needs a secure context). */
  async function copyCodes(): Promise<void> {
    if (!recoveryCodes) return;
    const clipboard = (navigator as { clipboard?: Clipboard }).clipboard;
    if (clipboard === undefined) {
      setCodesNotice({ tone: 'error', text: COPY_FAILED_COPY });
      return;
    }
    try {
      await clipboard.writeText(recoveryCodesText(recoveryCodes));
      setCodesNotice({ tone: 'ok', text: COPIED_COPY });
    } catch {
      setCodesNotice({ tone: 'error', text: COPY_FAILED_COPY });
    }
  }

  if (phase === 'recovery' && recoveryCodes) {
    return (
      <AuthCard
        title="Save your recovery codes"
        subtitle="These are shown once. Each code signs you in one time if you lose your authenticator — store them somewhere safe now."
      >
        <div style={{ display: 'grid', gap: 14 }}>
          <ul
            aria-label="Recovery codes"
            className="argus-auth__codes"
            style={{
              margin: 0,
              padding: '12px 14px',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '6px 16px',
              listStyle: 'none',
              fontSize: 14,
              letterSpacing: '0.04em',
              background: 'var(--auth-field-bg, #fff)',
              border: '1px solid var(--auth-field-border, #dcd6c8)',
              borderRadius: 8,
              color: 'var(--auth-field-text, #1c2536)',
            }}
          >
            {recoveryCodes.map((rc) => (
              <li key={rc}>{rc}</li>
            ))}
          </ul>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <SecondaryButton onClick={downloadCodes}>Download codes</SecondaryButton>
            <SecondaryButton onClick={() => void copyCodes()}>Copy codes</SecondaryButton>
          </div>
          {codesNotice === null ? null : codesNotice.tone === 'ok' ? (
            <FormStatus message={codesNotice.text} />
          ) : (
            <FormError message={codesNotice.text} />
          )}
          <CheckboxField
            label="I have saved my recovery codes. I understand they won't be shown again."
            checked={saved}
            onChange={setSaved}
          />
          <SubmitButton type="button" disabled={!saved} onClick={() => void afterSignIn(redirect)}>
            Continue
          </SubmitButton>
        </div>
      </AuthCard>
    );
  }

  if (phase === 'codes') {
    return (
      <AuthCard title="Two-factor authentication is on" subtitle="Your authenticator is set up.">
        {error ? (
          <div style={{ display: 'grid', gap: 12 }}>
            <FormError message={`We couldn't create your recovery codes. ${error}`} />
            <SubmitButton type="button" onClick={() => void loadRecoveryCodes()}>
              Try again
            </SubmitButton>
          </div>
        ) : (
          <p role="status" style={{ margin: 0, fontSize: 14 }}>
            Creating your recovery codes…
          </p>
        )}
      </AuthCard>
    );
  }

  if (phase === 'sign_in_again') {
    return (
      <AuthCard title="Sign in again to continue">
        <div style={{ display: 'grid', gap: 12 }}>
          <FormError message={ENROLLED_ELSEWHERE_COPY} />
          <SubmitButton type="button" onClick={() => void signInAgain()}>
            Sign in again
          </SubmitButton>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Set up two-factor authentication"
      subtitle="Scan the QR code with your authenticator app, then enter the code it shows."
    >
      {phase === 'enrolling' ? (
        <p role="status" style={{ margin: 0, fontSize: 14 }}>
          Preparing your enrolment…
        </p>
      ) : null}

      {enrol && qrSvg ? (
        <div style={{ display: 'grid', gap: 12 }}>
          <div
            aria-label="TOTP QR code"
            role="img"
            style={{
              background: '#fff',
              padding: 8,
              borderRadius: 8,
              border: '1px solid var(--auth-field-border, #dcd6c8)',
              width: 'fit-content',
            }}
            dangerouslySetInnerHTML={{ __html: qrSvg }}
          />
          {parseIssuer(enrol.otpauth_uri) ? (
            <p style={{ margin: 0, fontSize: 12, color: 'var(--auth-muted, #7a7365)' }}>
              Account: <strong>{parseIssuer(enrol.otpauth_uri)}</strong>
            </p>
          ) : null}
          <p style={{ margin: 0, fontSize: 12, color: 'var(--auth-muted, #7a7365)' }}>
            Can't scan? Enter this key manually:
            <br />
            <code
              aria-label="Manual secret key"
              className="argus-auth__codes"
              style={{
                fontSize: 13,
                wordBreak: 'break-all',
                color: 'var(--auth-field-text, #1c2536)',
              }}
            >
              {enrol.secret}
            </code>
          </p>
          <form
            onSubmit={handleConfirm}
            aria-label="Confirm authenticator code"
            style={{ display: 'grid', gap: 12 }}
          >
            <TextField
              label="Enter the 6-digit code to confirm"
              name="totp_code"
              value={code}
              onChange={(v) => setCode(v.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123456"
            />
            <FormError message={error} />
            <SubmitButton disabled={phase === 'saving' || code.length !== 6}>
              {phase === 'saving' ? 'Confirming…' : 'Confirm & continue'}
            </SubmitButton>
          </form>
        </div>
      ) : (
        <FormError message={error} />
      )}
    </AuthCard>
  );
}
